import { sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useSchoolModules } from '../hooks/useSchoolModules';
import {
  PlusIcon, XMarkIcon, PencilSquareIcon,
  WrenchScrewdriverIcon, FunnelIcon,
} from '@heroicons/react/24/outline';

/* ── Asset sub-options per category ─────────────────────────────────────── */
const ASSET_SUBS = {
  furniture:    ['Chair','Table','Desk','Cupboard','Shelf','Whiteboard','Notice Board','Staff Chair','Other'],
  electronics:  ['Computer/Laptop','Projector','Printer','Scanner','TV/Monitor','Speaker','CCTV Camera','UPS','Other'],
  vehicle:      ['School Van','Bus','Motorcycle','Car','Other'],
  sports:       ['Cricket Kit','Football','Basketball','Badminton','Table Tennis','Trophy Cabinet','Other'],
  lab:          ['Microscope','Test Tubes','Bunsen Burner','Science Kit','Chemistry Set','Other'],
  generator:    ['Main Generator','Backup Generator','Inverter','Battery','Other'],
  appliance:    ['AC Unit','Fan','Water Cooler','Water Dispenser','Refrigerator','Microwave','Other'],
  miscellaneous:['Musical Instrument','Intercom','Fire Extinguisher','Other'],
};

const CATEGORIES  = Object.keys(ASSET_SUBS);
const CONDITIONS  = ['excellent','good','fair','poor','damaged'];
const STATUSES    = ['active','inactive','disposed','under_repair'];

const CAT_COLORS = {
  furniture:'#8b5cf6', electronics:'#3b6ef8', vehicle:'#10c98e', sports:'#f59e0b',
  lab:'#6366f1', generator:'#ef4444', appliance:'#14b8a6', miscellaneous:'#9094a8',
};
const CAT_ICONS = {
  furniture:'🪑', electronics:'💻', vehicle:'🚌', sports:'⚽',
  lab:'🔬', generator:'⚡', appliance:'❄️', miscellaneous:'📦',
};
const COND_COLOR = { excellent:'#10c98e', good:'#3b6ef8', fair:'#f59e0b', poor:'#f97316', damaged:'#ef4444' };
const COND_BG    = { excellent:'#ecfdf5', good:'#eef2ff', fair:'#fffbeb', poor:'#fff7ed', damaged:'#fef2f2' };
const STAT_COLOR = { active:'#10c98e', inactive:'#9094a8', disposed:'#ef4444', under_repair:'#f59e0b' };
const STAT_BG    = { active:'#ecfdf5', inactive:'#f0f2f7', disposed:'#fef2f2', under_repair:'#fffbeb' };

const fmtMoney = (n) => (n === null || n === undefined || n === '') ? '—' : `Rs. ${Number(n).toLocaleString('en-PK')}`;
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';

const EMPTY_FORM = { name:'', category:'furniture', subcategory:'', assetCode:'', quantity:1, location:'', condition:'good', purchaseDate:'', purchasePrice:'', vendor:'', warrantyExpiry:'', description:'', status:'active', module:'school' };
const MODULE_LABELS = { school:{label:'School',icon:'🏫'}, hostel:{label:'Hostel',icon:'🏠'}, library:{label:'Library',icon:'📚'} };
const EMPTY_MAINT = { date:new Date().toISOString().slice(0,10), description:'', cost:'', performedBy:'', newCondition:'' };

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .as * { box-sizing:border-box; }
  .as { font-family:'Sora',sans-serif; color:#1a1d2e; animation:asFade .4s ease both; }
  @keyframes asFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .as-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .as-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .as-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; white-space:nowrap; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover   { background:#ef4444; color:white; }
  .btn-amber { background:#fffbeb; color:#f59e0b; border:1px solid #fde68a; }
  .btn-amber:hover { background:#f59e0b; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  /* Stat cards */
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon  { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; font-size:1rem; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .sc-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }

  /* Category tabs */
  .cat-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .cat-tab  { padding:5px 12px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; transition:all .15s; color:#6b7194; white-space:nowrap; }
  .cat-tab:hover  { border-color:#3b6ef8; color:#3b6ef8; }
  .cat-tab.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }

  /* Filter */
  .filter-bar { display:flex; gap:.6rem; flex-wrap:wrap; margin-bottom:1.2rem; align-items:center; }
  .filter-select { font-family:'Sora',sans-serif; font-size:.78rem; padding:7px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; }
  .filter-select:focus { border-color:#3b6ef8; background:white; }

  /* Desktop table */
  .tbl-wrap { overflow-x:auto; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 14px; text-align:center; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:11px 14px; font-size:.8rem; vertical-align:middle; text-align:center; }

  /* Badge (shared by table cells and mobile cards) */
  .ac-emoji { width:38px; height:38px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
  .ac-badge { display:inline-flex; padding:2px 8px; border-radius:99px; font-size:.65rem; font-weight:700; white-space:nowrap; }

  /* Mobile list */
  .asset-list { display:flex; flex-direction:column; gap:.7rem; }
  .asset-list-item { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem; }
  .ali-top  { display:flex; align-items:center; gap:10px; margin-bottom:.7rem; }
  .ali-info { flex:1; min-width:0; }
  .ali-name { font-size:.86rem; font-weight:700; color:#0f1123; }
  .ali-meta { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .ali-row  { display:flex; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f5f6fb; }
  .ali-row:last-of-type { border-bottom:none; }
  .ali-actions { display:flex; gap:6px; margin-top:.8rem; padding-top:.7rem; border-top:1px solid #f0f2f8; }

  .as-empty   { text-align:center; padding:3rem; color:#9094a8; font-size:.82rem; }
  .as-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  /* Modal is portaled to document.body, outside .as, so the .as * box-sizing
     reset above never reaches it — this page has no Tailwind preflight active,
     so without this every input/select/textarea defaults to content-box and
     renders wider than 100% by its own padding+border, overflowing its column. */
  .modal-backdrop, .modal-backdrop * { box-sizing:border-box; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:flex-start; justify-content:center; z-index:1000; padding:24px 16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; width:100%; max-width:600px; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; overflow:hidden; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; gap:8px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body { padding:20px 26px 20px 22px; max-height:62vh; overflow-y:auto; overflow-x:hidden; scrollbar-gutter:stable; box-sizing:border-box; }
  .modal-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }

  .form-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-full  { grid-column:1/-1; }
  .form-group { display:flex; flex-direction:column; gap:5px; }
  .form-label { font-size:.7rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { resize:vertical; min-height:60px; }

  /* Maintenance log */
  .maint-log { background:#fafbff; border-radius:10px; border:1px solid #e8ebf4; padding:10px 12px; margin-bottom:8px; }
  .maint-log-date { font-size:.7rem; font-weight:700; color:#3b6ef8; font-family:'JetBrains Mono',monospace; }
  .maint-log-desc { font-size:.78rem; color:#1a1d2e; margin-top:3px; }
  .maint-log-meta { font-size:.68rem; color:#9094a8; margin-top:3px; }
  .section-divider { font-size:.72rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.07em; margin:12px 0 8px; padding-bottom:6px; border-bottom:1px solid #f0f2f8; }

  /* Responsive */
  @media (max-width:900px) { .stat-grid{grid-template-columns:repeat(2,1fr);} }
  @media (max-width:768px) {
    .as-title{font-size:1.1rem;}
    .tbl-wrap { display:none; }
    .asset-list { display:flex; }
    .form-grid  { grid-template-columns:1fr; }
    .modal-foot { justify-content:stretch; }
    .modal-foot .btn { flex:1; justify-content:center; }
    .modal-body { max-height:70vh; }
  }
  @media (min-width:769px) { .asset-list { display:none; } .tbl-wrap { display:block; } }
  @media (max-width:480px) { .stat-grid{grid-template-columns:1fr 1fr;} }
`;

const Assets = () => {
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const showModuleTabs = libraryEnabled || hostelEnabled;
  const [assets,       setAssets]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modal,        setModal]        = useState(false);
  const [maintModal,   setMaintModal]   = useState(false);
  const [selectedAsset,setSelectedAsset]= useState(null);
  const [editing,      setEditing]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [maintForm,    setMaintForm]    = useState(EMPTY_MAINT);
  const [filterCat,    setFilterCat]    = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterModule, setFilterModule] = useState('');

  useEffect(()=>{fetchAssets();},[filterCat,filterStatus,filterModule]);

  const fetchAssets = async()=>{
    try{
      setLoading(true);
      const params={};
      if(filterCat)    params.category=filterCat;
      if(filterStatus) params.status=filterStatus;
      if(filterModule) params.module=filterModule;
      const{data}=await api.get('/admin/assets',{params});
      setAssets(data||[]);
    } catch{ toast.error('Failed to load assets'); }
    finally{ setLoading(false); }
  };

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleCatChange = (cat) => setForm(f=>({...f,category:cat,subcategory:''}));

  const openAdd  = ()=>{ setEditing(null); setForm(EMPTY_FORM); setModal(true); };
  const openEdit = (a)=>{
    setEditing(a);
    setForm({
      name:a.name, category:a.category, subcategory:a.subcategory||'',
      assetCode:a.assetCode||'', quantity:a.quantity||1, location:a.location||'',
      condition:a.condition||'good', purchaseDate:a.purchaseDate?.slice(0,10)||'',
      purchasePrice:a.purchasePrice||'', vendor:a.vendor||'',
      warrantyExpiry:a.warrantyExpiry?.slice(0,10)||'',
      description:a.description||'', status:a.status||'active',
      module:a.module||'school',
    });
    setModal(true);
  };
  const openMaint=(a)=>{ setSelectedAsset(a); setMaintForm(EMPTY_MAINT); setMaintModal(true); };

  const handleSave = async()=>{
    if(!form.name) return toast.error('Name is required');
    try{
      setSaving(true);
      if(editing){
        const{data}=await api.put(`/admin/assets/${editing._id}`,form);
        setAssets(prev=>prev.map(a=>a._id===editing._id?data:a));
        toast.success('Updated');
      } else {
        const{data}=await api.post('/admin/assets',form);
        setAssets(prev=>[data,...prev]);
        toast.success('Asset added');
      }
      setModal(false);
    } catch(err){ toast.error(err.response?.data?.message||'Failed to save'); }
    finally{ setSaving(false); }
  };

  const handleMaintSave = async()=>{
    if(!maintForm.description) return toast.error('Description is required');
    try{
      setSaving(true);
      const{data}=await api.post(`/admin/assets/${selectedAsset._id}/maintenance`,maintForm);
      setAssets(prev=>prev.map(a=>a._id===selectedAsset._id?data:a));
      toast.success('Log added');
      setMaintModal(false);
    } catch{ toast.error('Failed to save'); }
    finally{ setSaving(false); }
  };

  const total       = assets.length;
  const active      = assets.filter(a=>a.status==='active').length;
  const underRepair = assets.filter(a=>a.status==='under_repair').length;
  const totalValue  = assets.reduce((s,a)=>s+(a.purchasePrice||0),0);
  const subs        = ASSET_SUBS[form.category]||[];

  return (
    <div className="as"><style>{style}</style>

      <div className="as-header">
        <div>
          <div className="as-title">Assets</div>
          <div className="as-sub">Track school property — furniture, electronics, vehicles & more</div>
        </div>
        <button className="btn btn-blue" onClick={openAdd}><PlusIcon/>Add Asset</button>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-card"><div className="sc-icon" style={{background:'#eef2ff'}}>📦</div><div className="sc-label">Total Assets</div><div className="sc-value">{total}</div><div className="sc-sub">tracked items</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#ecfdf5'}}>✅</div><div className="sc-label">Active</div><div className="sc-value">{active}</div><div className="sc-sub">in use</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#fffbeb'}}>🔧</div><div className="sc-label">Under Repair</div><div className="sc-value">{underRepair}</div><div className="sc-sub">being serviced</div></div>
        <div className="stat-card"><div className="sc-icon" style={{background:'#f3f0ff'}}>💰</div><div className="sc-label">Total Value</div><div className="sc-value" style={{fontSize:'1rem'}}>{fmtMoney(totalValue)}</div><div className="sc-sub">purchase price</div></div>
      </div>

      {/* Module tabs — only when Owner has enabled Library/Hostel for this school */}
      {showModuleTabs && (
        <div className="cat-tabs">
          <button className={`cat-tab${!filterModule?' active':''}`} onClick={()=>setFilterModule('')}>All</button>
          {hostelEnabled  && <button className={`cat-tab${filterModule==='hostel'?' active':''}`}  onClick={()=>setFilterModule('hostel')}>🏠 Hostel</button>}
          {libraryEnabled && <button className={`cat-tab${filterModule==='library'?' active':''}`} onClick={()=>setFilterModule('library')}>📚 Library</button>}
          <button className={`cat-tab${filterModule==='school'?' active':''}`} onClick={()=>setFilterModule('school')}>🏫 School</button>
        </div>
      )}

      {/* Category tabs */}
      <div className="cat-tabs">
        <button className={`cat-tab${!filterCat?' active':''}`} onClick={()=>setFilterCat('')}>All</button>
        {sortAsc(CATEGORIES).map(c=>(
          <button key={c} className={`cat-tab${filterCat===c?' active':''}`} onClick={()=>setFilterCat(c)}>
            {CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}
          </button>
        ))}
      </div>

      {/* Status filter */}
      <div className="filter-bar">
        <FunnelIcon style={{width:15,height:15,stroke:'#9094a8',flexShrink:0}}/>
        <select className="filter-select" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {sortAsc(STATUSES, s=>s.replace('_',' ')).map(s=><option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={()=>{setFilterCat('');setFilterStatus('');}}>Reset</button>
      </div>

      {loading ? (
        <div className="as-loading"><div className="spinner"/>Loading…</div>
      ) : assets.length===0 ? (
        <div className="as-empty">No assets found. <button className="btn btn-blue btn-sm" style={{marginLeft:8}} onClick={openAdd}><PlusIcon/>Add First</button></div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th><th>Category</th><th>Sub-type</th><th>Qty</th><th>Location</th>
                  <th>Condition</th><th>Status</th><th>Value</th><th>Purchased</th><th>Warranty</th><th>Vendor</th><th>Logs</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map(a=>(
                  <tr key={a._id}>
                    <td style={{fontWeight:600}}>
                      {a.name}
                      {a.assetCode&&<div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.66rem',color:'#9094a8',marginTop:2}}>#{a.assetCode}</div>}
                    </td>
                    <td><span className="ac-badge" style={{background:CAT_COLORS[a.category]+'22',color:CAT_COLORS[a.category]}}>{CAT_ICONS[a.category]} {a.category}</span></td>
                    <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.subcategory||'—'}</td>
                    <td>{a.quantity||1}</td>
                    <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.location||'—'}</td>
                    <td><span className="ac-badge" style={{background:COND_BG[a.condition],color:COND_COLOR[a.condition]}}>{a.condition}</span></td>
                    <td><span className="ac-badge" style={{background:STAT_BG[a.status],color:STAT_COLOR[a.status]}}>{a.status.replace('_',' ')}</span></td>
                    <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{fmtMoney(a.purchasePrice)}</td>
                    <td style={{fontSize:'.76rem',color:'#6b7194'}}>{fmtDate(a.purchaseDate)}</td>
                    <td style={{fontSize:'.76rem',color:a.warrantyExpiry&&new Date(a.warrantyExpiry)<new Date()?'#ef4444':'#6b7194'}}>{fmtDate(a.warrantyExpiry)}</td>
                    <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.vendor||'—'}</td>
                    <td style={{fontSize:'.76rem',color:'#6b7194'}}>{a.maintenanceLogs?.length||0}</td>
                    <td>
                      <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                        <button className="btn btn-amber btn-sm" onClick={()=>openMaint(a)}><WrenchScrewdriverIcon/></button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(a)}><PencilSquareIcon/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile list */}
          <div className="asset-list">
            {assets.map(a=>(
              <div key={a._id} className="asset-list-item">
                <div className="ali-top">
                  <div className="ac-emoji" style={{background:CAT_COLORS[a.category]+'22'}}>{CAT_ICONS[a.category]}</div>
                  <div className="ali-info">
                    <div className="ali-name">{a.name}</div>
                    <div className="ali-meta">{a.subcategory||a.category} · {a.location||'No location'}</div>
                  </div>
                  <span className="ac-badge" style={{background:STAT_BG[a.status],color:STAT_COLOR[a.status],flexShrink:0}}>{a.status.replace('_',' ')}</span>
                </div>
                <div className="ali-row"><span style={{color:'#9094a8'}}>Condition</span><span className="ac-badge" style={{background:COND_BG[a.condition],color:COND_COLOR[a.condition]}}>{a.condition}</span></div>
                {a.purchasePrice>0&&<div className="ali-row"><span style={{color:'#9094a8'}}>Value</span><span style={{fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{fmtMoney(a.purchasePrice)}</span></div>}
                {a.purchaseDate&&<div className="ali-row"><span style={{color:'#9094a8'}}>Purchased</span><span>{fmtDate(a.purchaseDate)}</span></div>}
                <div className="ali-actions">
                  <button className="btn btn-amber btn-sm" style={{flex:1,justifyContent:'center'}} onClick={()=>openMaint(a)}><WrenchScrewdriverIcon/>Maintain</button>
                  <button className="btn btn-ghost btn-sm" style={{flex:1,justifyContent:'center'}} onClick={()=>openEdit(a)}><PencilSquareIcon/>Edit</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {modal&&createPortal(
        <div className="modal-backdrop" onClick={()=>setModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{editing?'✏️ Edit Asset':'➕ Add Asset'}</div>
              <button className="modal-close" onClick={()=>setModal(false)}><XMarkIcon/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                {showModuleTabs && (
                  <div className="form-group">
                    <label className="form-label">Belongs To</label>
                    <select className="form-select" value={form.module} onChange={e=>setF('module',e.target.value)}>
                      <option value="school">🏫 School</option>
                      {hostelEnabled  && <option value="hostel">🏠 Hostel</option>}
                      {libraryEnabled && <option value="library">📚 Library</option>}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e=>handleCatChange(e.target.value)}>
                    {CATEGORIES.map(c=><option key={c} value={c}>{CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Type / Sub-category *</label>
                  <select className="form-select" value={form.subcategory} onChange={e=>setF('subcategory',e.target.value)}>
                    <option value="">— Select type —</option>
                    {subs.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Name *</label>
                  <input className="form-input" value={form.name}
                    placeholder={form.subcategory?`e.g. ${form.subcategory} - Room 5`:'e.g. Wooden Chair - Room 5'}
                    onChange={e=>setF('name',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Asset Code / Tag</label>
                  <input className="form-input" value={form.assetCode} placeholder="e.g. CHR-001" onChange={e=>setF('assetCode',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input className="form-input" type="number" min={1} value={form.quantity} onChange={e=>setF('quantity',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input className="form-input" value={form.location} placeholder="e.g. Room 5, Block A" onChange={e=>setF('location',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Condition</label>
                  <select className="form-select" value={form.condition} onChange={e=>setF('condition',e.target.value)}>
                    {CONDITIONS.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e=>setF('status',e.target.value)}>
                    {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Purchase Price (Rs.)</label>
                  <input className="form-input" type="number" value={form.purchasePrice} placeholder="0" onChange={e=>setF('purchasePrice',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Purchase Date</label>
                  <input className="form-input" type="date" value={form.purchaseDate} onChange={e=>setF('purchaseDate',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Vendor / Supplier</label>
                  <input className="form-input" value={form.vendor} placeholder="e.g. Ali Furniture" onChange={e=>setF('vendor',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Warranty Expiry</label>
                  <input className="form-input" type="date" value={form.warrantyExpiry} onChange={e=>setF('warrantyExpiry',e.target.value)}/>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Description / Notes</label>
                  <textarea className="form-textarea" value={form.description} placeholder="Optional notes..." onChange={e=>setF('description',e.target.value)}/>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSave} disabled={saving}>{saving?'Saving…':editing?'Save Changes':'Add Asset'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Maintenance Modal */}
      {maintModal&&selectedAsset&&createPortal(
        <div className="modal-backdrop" onClick={()=>setMaintModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">🔧 Maintenance — {selectedAsset.name}</div>
              <button className="modal-close" onClick={()=>setMaintModal(false)}><XMarkIcon/></button>
            </div>
            <div className="modal-body">
              {selectedAsset.maintenanceLogs?.length>0&&(
                <>
                  <div className="section-divider">Previous Logs ({selectedAsset.maintenanceLogs.length})</div>
                  {[...selectedAsset.maintenanceLogs].reverse().slice(0,3).map((log,i)=>(
                    <div key={i} className="maint-log">
                      <div className="maint-log-date">{fmtDate(log.date)}</div>
                      <div className="maint-log-desc">{log.description}</div>
                      <div className="maint-log-meta">
                        {log.cost>0&&`Cost: Rs. ${log.cost.toLocaleString()}`}
                        {log.performedBy&&` · By: ${log.performedBy}`}
                      </div>
                    </div>
                  ))}
                </>
              )}
              <div className="section-divider">Add New Log</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={maintForm.date} onChange={e=>setMaintForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Cost (Rs.)</label>
                  <input className="form-input" type="number" value={maintForm.cost} placeholder="0" onChange={e=>setMaintForm(f=>({...f,cost:e.target.value}))}/>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Description *</label>
                  <textarea className="form-textarea" value={maintForm.description} placeholder="What was done? e.g. Engine oil changed, filter replaced" onChange={e=>setMaintForm(f=>({...f,description:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Performed By</label>
                  <input className="form-input" value={maintForm.performedBy} placeholder="Technician / vendor name" onChange={e=>setMaintForm(f=>({...f,performedBy:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Update Condition</label>
                  <select className="form-select" value={maintForm.newCondition} onChange={e=>setMaintForm(f=>({...f,newCondition:e.target.value}))}>
                    <option value="">— No change —</option>
                    {CONDITIONS.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={()=>setMaintModal(false)}>Cancel</button>
              <button className="btn btn-amber" onClick={handleMaintSave} disabled={saving}>{saving?'Saving…':'Save Log'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default Assets;